// CE-04g-UI-pivots-overlay-renderer — pure helpers backing the bounded
// session segments that replace `candleSeries.createPriceLine` for pivot
// rendering. Co-evolves with the chart-layer pivot pipeline in
// `src/components/chart/TradeChart.jsx` (Effect 1 mints; Effect 2 drifts).
//
// Session-key derivation mirrors `filterPivotsToTodayEtSession` in
// TradeChart.jsx: prefer `bar.rawEpochMs` when finite, otherwise fall back
// to `bar.time * 1000` so legacy seed bars without rawEpochMs are still
// routed through `getEtSessionDateKey`.
//
// CE-04g-UI-pivots-overlay-labels-and-extent — visual-fidelity follow-up:
// segments now extend through the next ET RTH open and the level label
// renders as an inline pill at the segment's right endpoint (inside the
// chart pane). The price-axis label is suppressed unconditionally — the
// existing `axisLabelVisible` flag continues to flow through the pipeline
// but now controls the inline-pill render decision instead of LWC's
// `lastValueVisible`. See `computePivotPillPositions` below.

import { getEtSessionDateKey } from './sessionBoundary.js'
import { toChartTimestamp } from '../timezone.js'

function sessionKeyForBar(bar) {
  if (!bar) return null
  if (typeof bar.rawEpochMs === 'number' && Number.isFinite(bar.rawEpochMs)) {
    return getEtSessionDateKey(bar.rawEpochMs)
  }
  if (typeof bar.time === 'number' && Number.isFinite(bar.time)) {
    return getEtSessionDateKey(bar.time * 1000)
  }
  return null
}

/**
 * Walk `effectiveBars` from the end and return the LWC `time` of the first
 * bar whose session key equals the last bar's session key. If every bar
 * shares the same session key, returns the first bar's `time`. Returns
 * null when the last bar yields no usable session key (neither
 * `rawEpochMs` nor `time` resolves).
 *
 * @param {Array<{time:number, rawEpochMs?:number}>} effectiveBars
 * @returns {number | null}
 */
export function findCurrentSessionStartTime(effectiveBars) {
  if (!Array.isArray(effectiveBars) || effectiveBars.length === 0) return null
  const lastBar = effectiveBars[effectiveBars.length - 1]
  const lastKey = sessionKeyForBar(lastBar)
  if (!lastKey) return null
  let firstBarOfSession = lastBar
  for (let i = effectiveBars.length - 1; i >= 0; i--) {
    const bar = effectiveBars[i]
    if (sessionKeyForBar(bar) === lastKey) {
      firstBarOfSession = bar
    } else {
      break
    }
  }
  return firstBarOfSession?.time ?? null
}

/**
 * Build the 2-point `[{time,value},{time,value}]` array passed to
 * `series.setData` to render a flat horizontal bounded segment. Returns
 * null when either time is missing OR `segmentEndTime < sessionStartTime`.
 *
 * @param {number|null|undefined} sessionStartTime
 * @param {number|null|undefined} segmentEndTime
 * @param {number} price
 * @returns {Array<{time:number, value:number}> | null}
 */
export function buildPivotSegmentData(sessionStartTime, segmentEndTime, price) {
  if (sessionStartTime == null || segmentEndTime == null) return null
  if (typeof sessionStartTime !== 'number' || !Number.isFinite(sessionStartTime)) return null
  if (typeof segmentEndTime !== 'number' || !Number.isFinite(segmentEndTime)) return null
  if (segmentEndTime < sessionStartTime) return null
  return [
    { time: sessionStartTime, value: price },
    { time: segmentEndTime, value: price },
  ]
}

/**
 * Return the LWC options bag for a pivot overlay line series. Bakes in
 * the fixed visual contract (no price line, no crosshair marker,
 * autoscale-disabled, NO price-axis label).
 *
 * CE-04g-UI-pivots-overlay-labels-and-extent — `lastValueVisible` is
 * `false` unconditionally so the price-axis pivot label disappears.
 * CE-04g-UI-pivots-tv-fidelity-fix — `title` is forced to `''` for the
 * same reason: LWC renders any non-empty `title` as a stacked label on
 * the right price axis even when `lastValueVisible: false`. The display
 * label is rendered by the HTML overlay pills, not the price axis.
 * CE-04g-UI-pivots-rth-and-style-fixes — `lineWidth` is now a caller-
 * provided option (rounded + clamped to LWC's integer 1-4 range). Falls
 * back to 2 when omitted so legacy tests / fixtures keep working.
 *
 * The `title` and `axisLabelVisible` args are preserved on the function
 * signature for caller compatibility (Effect 1 / Effect 2 still compute
 * them and pass them through), but neither is forwarded to LWC.
 *
 * @param {object} args
 * @param {string} args.color
 * @param {number} args.lineStyleEnum
 * @param {string} [args.title]              preserved for caller API
 *                                            compatibility; intentionally
 *                                            NOT forwarded to LWC.
 * @param {boolean} [args.axisLabelVisible]  preserved for caller API
 *                                            compatibility; no longer used
 *                                            inside this function.
 * @param {number} [args.lineWidth]          integer 1-4; defaults to 2.
 * @returns {object}
 */
// eslint-disable-next-line no-unused-vars
export function lineSeriesOptionsForPivot({ color, lineStyleEnum, title, axisLabelVisible, lineWidth }) {
  const resolvedLineWidth = Number.isFinite(lineWidth)
    ? Math.min(4, Math.max(1, Math.round(lineWidth)))
    : 2
  return {
    color,
    lineStyle: lineStyleEnum,
    lineWidth: resolvedLineWidth,
    title: '',
    priceLineVisible: false,
    lastValueVisible: false,
    crosshairMarkerVisible: false,
    autoscaleInfoProvider: () => null,
  }
}

/**
 * CE-04g-UI-pivots-tv-fidelity-fix — color helper for the subtle inline
 * pivot labels. Converts a `#RRGGBB` hex string into an `rgba(r, g, b,
 * alpha)` string. Non-string input falls back to neutral gray
 * (`rgba(156, 163, 175, alpha)`). Non-hex strings pass through unchanged
 * so already-`rgba(...)` colors or CSS named colors stay intact.
 *
 * CE-04g-UI-pivots-rth-and-style-fixes — now also accepts `#RRGGBBAA`
 * (the 8-digit form returned by `applyOpacityToColor`). The source alpha
 * is multiplied by the `alpha` argument, so the user's per-component
 * opacity composes multiplicatively with the 0.72 label-shading default.
 *
 * @param {unknown} color
 * @param {number} [alpha=0.72]
 * @returns {string}
 */
export function withAlpha(color, alpha = 0.72) {
  if (typeof color !== 'string') return `rgba(156, 163, 175, ${alpha})`
  const hex = color.trim()
  const m = hex.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i)
  if (!m) return color
  const n = Number.parseInt(m[1], 16)
  const r = (n >> 16) & 255
  const g = (n >> 8) & 255
  const b = n & 255
  const sourceAlpha = m[2] ? Number.parseInt(m[2], 16) / 255 : 1
  const finalAlpha = Math.max(0, Math.min(1, sourceAlpha * alpha))
  return `rgba(${r}, ${g}, ${b}, ${finalAlpha})`
}

function readHolidaySet(tradingCalendar) {
  if (!tradingCalendar) return null
  const raw = tradingCalendar.holidays
  if (!Array.isArray(raw) || raw.length === 0) return null
  const out = new Set()
  for (const h of raw) {
    if (typeof h === 'string') out.add(h)
    else if (h && typeof h.date === 'string') out.add(h.date)
  }
  return out.size === 0 ? null : out
}

function parseYmd(key) {
  const y = Number(key.slice(0, 4))
  const mo = Number(key.slice(5, 7))
  const d = Number(key.slice(8, 10))
  return Date.UTC(y, mo - 1, d)
}

function ymdFromUtcEpoch(ms) {
  const d = new Date(ms)
  const y = d.getUTCFullYear()
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function addDaysToKey(key, days) {
  return ymdFromUtcEpoch(parseYmd(key) + days * 86400000)
}

/**
 * CE-04g-UI-pivots-overlay-labels-and-extent — forward mirror of
 * `getPreviousEtTradingDayKey` from `sessionBoundary.js`. Walks one ET
 * calendar day FORWARD from `etDateKey`, skipping weekends and any dates
 * in `tradingCalendar.holidays`. Returns the resulting YYYY-MM-DD key, or
 * null when the 14-iteration safety cap is hit. Lives in this module
 * (not in sessionBoundary.js) so the session-boundary surface stays
 * locked.
 *
 * @param {string} etDateKey  YYYY-MM-DD ET calendar date
 * @param {{ holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @returns {string | null}
 */
export function getNextEtTradingDayKey(etDateKey, tradingCalendar) {
  if (typeof etDateKey !== 'string' || etDateKey.length < 10) return null
  const holidays = readHolidaySet(tradingCalendar)
  let candidate = etDateKey
  for (let i = 0; i < 14; i++) {
    candidate = addDaysToKey(candidate, 1)
    const dow = new Date(parseYmd(candidate)).getUTCDay()
    if (dow === 0 || dow === 6) continue
    if (holidays && holidays.has(candidate)) continue
    return candidate
  }
  return null
}

/**
 * Return the LWC chart-time UTCTimestamp (seconds) for 09:30 ET on the
 * next trading day after `etDateKey`. Uses the canonical
 * `toChartTimestamp(ibkrTimeStr, timezone)` from `src/lib/timezone.js`
 * (see line 146 — chart-time is the USER DISPLAY TIMEZONE as fake-UTC,
 * NOT ET wall clock as UTC). The candle-store hydration path uses the
 * same conversion (see `src/lib/candleStore.js:281` `toCandles`), so the
 * value returned here is directly comparable to the bar times the chart
 * already renders.
 *
 * Returns null when the calendar walker exhausts (no future trading day
 * within 14 iterations) or when `toChartTimestamp` returns a non-finite
 * value.
 *
 * @param {string} etDateKey
 * @param {{ holidays?: Array<string|{date:string}> } | null | undefined} tradingCalendar
 * @param {string} timezone  IANA tz string (user's display timezone)
 * @returns {number | null}
 */
export function nextRthOpenChartTime(etDateKey, tradingCalendar, timezone) {
  const nextDateKey = getNextEtTradingDayKey(etDateKey, tradingCalendar)
  if (!nextDateKey) return null
  const yyyymmdd = nextDateKey.replace(/-/g, '')
  const result = toChartTimestamp(`${yyyymmdd} 09:30:00 US/Eastern`, timezone)
  return Number.isFinite(result) && result > 0 ? result : null
}

/**
 * CE-04g-UI-pivots-rth-and-style-fixes — sibling to `nextRthOpenChartTime`.
 * Returns the LWC chart-time UTCTimestamp (seconds) for 16:00 ET on the
 * given ET trading day. Uses the same `toChartTimestamp` projection so
 * the result is directly comparable to chart-rendered bar times. Used
 * by `buildPivotTimelineSegmentData` to bound the future-intraday fill
 * loop at today's RTH close — points beyond 16:00 ET would land in the
 * overnight period where the chart has no candles.
 *
 * @param {string} etDateKey  YYYY-MM-DD ET calendar date
 * @param {string} timezone   IANA tz string (user's display timezone)
 * @returns {number | null}
 */
export function rthCloseChartTime(etDateKey, timezone) {
  if (typeof etDateKey !== 'string' || etDateKey.length < 10) return null
  const yyyymmdd = etDateKey.replace(/-/g, '')
  const result = toChartTimestamp(`${yyyymmdd} 16:00:00 US/Eastern`, timezone)
  return Number.isFinite(result) && result > 0 ? result : null
}

/**
 * CE-04g-UI-pivots-tv-fidelity-fix — infer the chart's native bar
 * interval (in seconds) from the in-memory bars stream. Computes the
 * median of consecutive `time` deltas, discarding any delta larger than
 * 6h so RTH-to-RTH overnight gaps don't poison the median while still
 * letting 65-minute (3900s) bars through. Returns null when there is
 * insufficient data to infer the interval.
 *
 * @param {Array<{time:number}> | null | undefined} effectiveBars
 * @returns {number | null}
 */
export function inferChartIntervalSeconds(effectiveBars) {
  if (!Array.isArray(effectiveBars) || effectiveBars.length < 2) return null
  const deltas = []
  for (let i = 1; i < effectiveBars.length; i++) {
    const prev = effectiveBars[i - 1]?.time
    const next = effectiveBars[i]?.time
    if (Number.isFinite(prev) && Number.isFinite(next) && next > prev) {
      const delta = next - prev
      if (delta > 0 && delta <= 6 * 60 * 60) deltas.push(delta)
    }
  }
  if (deltas.length === 0) return null
  deltas.sort((a, b) => a - b)
  return deltas[Math.floor(deltas.length / 2)]
}

/**
 * CE-04g-UI-pivots-rth-and-style-fixes — RTH-only timeline shape.
 * Emits the pivot-overlay setData payload as:
 *   (a) existing candle times within
 *       `[sessionStartTime, currentRthCloseTime]` — one point per bar
 *       so LWC's time scale aligns the pivot line with each candle.
 *   (b) future intraday slots from the latest existing time up to,
 *       but NOT including, `currentRthCloseTime` (close-exclusive,
 *       per CE-04g-UI-pivots-timeline-architecture) at the inferred
 *       bar interval. LWC's time scale only allocates a visual column
 *       when at least one series carries a point at that timestamp;
 *       populating future RTH slots forces visible columns through
 *       today's last in-session bar. Emitting the close timestamp
 *       itself would add a synthetic 15:00 CT / 16:00 ET slot.
 *   (c) a single endpoint at `segmentEndTime` (next-day RTH open). The
 *       gap between (b) and (c) intentionally has NO intermediate
 *       points, so LWC compresses the overnight period into a single
 *       logical slot — matching TradingView's session-aware time axis.
 *
 * Returns null on any non-finite anchor, when `effectiveBars` is not an
 * array, or when `segmentEndTime < sessionStartTime`. Falls back to a
 * 300s interval when `inferChartIntervalSeconds` returns null.
 *
 * @param {object} args
 * @param {number|null|undefined} args.sessionStartTime
 * @param {number|null|undefined} args.currentRthCloseTime
 * @param {number|null|undefined} args.segmentEndTime
 * @param {number} args.price
 * @param {Array<{time:number}>} args.effectiveBars
 * @returns {Array<{time:number, value:number}> | null}
 */
export function buildPivotTimelineSegmentData({
  sessionStartTime,
  currentRthCloseTime,
  segmentEndTime,
  price,
  effectiveBars,
}) {
  if (sessionStartTime == null || currentRthCloseTime == null || segmentEndTime == null) return null
  if (!Number.isFinite(sessionStartTime)
      || !Number.isFinite(currentRthCloseTime)
      || !Number.isFinite(segmentEndTime)) return null
  if (!Number.isFinite(price)) return null
  if (segmentEndTime < sessionStartTime) return null
  if (!Array.isArray(effectiveBars)) return null

  const interval = inferChartIntervalSeconds(effectiveBars) ?? 5 * 60
  const out = []

  // (a) Existing candle times within [sessionStartTime, currentRthCloseTime].
  for (const bar of effectiveBars) {
    if (!bar || !Number.isFinite(bar.time)) continue
    if (bar.time < sessionStartTime) continue
    if (bar.time > currentRthCloseTime) break  // bars sorted ascending; stop early
    out.push({ time: bar.time, value: price })
  }

  // (b) Future intraday slots from the latest existing time up to,
  //     but not including, today's RTH close. Pivot lines are real
  //     LWC line series, so emitting the RTH close timestamp puts a
  //     synthetic point on the chart's shared time scale (e.g. an
  //     extra 15:00 CT / 16:00 ET slot on a 5m RTH chart whose last
  //     real candle is 14:55 CT). The single next-session endpoint
  //     at `segmentEndTime` is still appended after this loop.
  const lastExistingTime = out.length > 0 ? out[out.length - 1].time : sessionStartTime
  for (let t = lastExistingTime + interval; t < currentRthCloseTime; t += interval) {
    out.push({ time: t, value: price })
  }

  // Guarantee the session-start anchor: prepend it when neither the
  // candle-iteration nor the future-fill produced it.
  if (out.length === 0 || out[0].time !== sessionStartTime) {
    out.unshift({ time: sessionStartTime, value: price })
  }

  // (c) Single endpoint at next-day RTH open. No intermediate overnight
  //     points — LWC will compress the gap.
  if (out[out.length - 1].time !== segmentEndTime) {
    out.push({ time: segmentEndTime, value: price })
  }

  return out
}

/**
 * Helper for callers that want a uniformly-shaped per-preset bucket.
 * Every init/reset site in TradeChart.jsx (Effect 1 first-seen preset,
 * Effect 2 rollover reset) should use this so the bucket shape stays
 * consistent across init sites. The widened metadata (segmentEndTime,
 * levelsByDisplayKey) is what `computePivotPillPositions` reads from.
 *
 * @returns {{
 *   sessionStartTime: number|null,
 *   segmentEndTime: number|null,
 *   handles: Record<string, object>,
 *   levelsByDisplayKey: Record<string, {
 *     price: number,
 *     color: string,
 *     axisLabelVisible: boolean,
 *     priceScaleMinMove: number,
 *   }>,
 * }}
 */
export function emptyPivotOverlayBucket() {
  return {
    sessionStartTime: null,
    segmentEndTime: null,
    handles: {},
    levelsByDisplayKey: {},
    // CE-04g-UI-pivots-rth-and-style-fixes — indicator-level pill font
    // size; written once per preset per pass from `ind.style.font_size`
    // and consumed by `computePivotPillPositions` to drive each pill's
    // `fontSize` field.
    fontSize: 11,
  }
}

// CE-04g-UI-pivots-tv-fidelity-fix — visible gap (in CSS px) between the
// segment's right endpoint and the inline pill label, matching TV Classic
// Daily's small breathing room before the label text.
const PIVOT_LABEL_GAP_PX = 12

export function decimalsFromMinMove(minMove) {
  if (!Number.isFinite(minMove) || minMove <= 0) return 2
  const s = String(minMove)
  if (s.includes('e') || s.includes('E')) {
    const m = s.match(/e-?(\d+)/i)
    if (m) return Number(m[1])
    return 2
  }
  const dot = s.indexOf('.')
  return dot === -1 ? 0 : (s.length - dot - 1)
}

export function formatPrice(value, minMove) {
  if (!Number.isFinite(value)) return ''
  const decimals = decimalsFromMinMove(minMove)
  return value.toFixed(decimals)
}

/**
 * CE-04g-UI-pivots-overlay-labels-and-extent — compute pill descriptors
 * for the inline overlay rendered inside the chart pane at each pivot
 * segment's right endpoint. Reads ALL per-level metadata from the
 * widened per-preset bucket (no need to thread style/compute-key maps
 * through). Skips levels whose `axisLabelVisible` is false (axis-dedupe
 * suppression), whose handle is missing, or whose coordinates can't be
 * projected (off-screen / null minMove / non-finite price).
 *
 * @param {object} args
 * @param {Record<string, {
 *   sessionStartTime: number|null,
 *   segmentEndTime: number|null,
 *   handles: Record<string, object>,
 *   levelsByDisplayKey: Record<string, {
 *     price: number,
 *     color: string,
 *     axisLabelVisible: boolean,
 *     priceScaleMinMove: number,
 *   }>,
 * }>} args.pivotMap
 * @param {Array<{kind?:string, presetId:string, style?:{visible?:boolean}}>} args.indicators
 * @param {{ timeScale: () => { timeToCoordinate: (t:number)=>number|null, width?: () => number } }} args.chartApi
 * @param {{ priceToCoordinate: (p:number)=>number|null }} args.candleSeriesApi
 * @param {{ current?: { clientWidth?: number } } | null} [args.chartContainerRef]
 * @returns {Array<{presetId:string, displayKey:string, left:number, top:number, color:string, text:string}>}
 */
export function computePivotPillPositions({
  pivotMap,
  indicators,
  chartApi,
  candleSeriesApi,
  chartContainerRef,
}) {
  if (!pivotMap || !chartApi || !candleSeriesApi) return []
  const ts = chartApi.timeScale?.()
  if (!ts || typeof ts.timeToCoordinate !== 'function') return []

  const chartWidth =
    (typeof ts.width === 'function' ? ts.width() : null)
    ?? chartContainerRef?.current?.clientWidth
    ?? 0

  const out = []
  if (!Array.isArray(indicators)) return out

  for (const ind of indicators) {
    if (!ind || ind.kind !== 'pivots') continue
    if (ind.style?.visible === false) continue
    const bucket = pivotMap[ind.presetId]
    if (!bucket) continue
    const segmentEndTime = bucket.segmentEndTime
    if (segmentEndTime == null || !Number.isFinite(segmentEndTime)) continue
    const handles = bucket.handles || {}
    const levels = bucket.levelsByDisplayKey || {}

    for (const dk of Object.keys(levels)) {
      const meta = levels[dk]
      if (!meta) continue
      if (meta.axisLabelVisible === false) continue
      if (!handles[dk]) continue
      if (!Number.isFinite(meta.price)) continue

      const endpointLeft = ts.timeToCoordinate(segmentEndTime)
      if (endpointLeft == null || !Number.isFinite(endpointLeft)) continue
      const top = candleSeriesApi.priceToCoordinate(meta.price)
      if (top == null || !Number.isFinite(top)) continue
      // CE-04g-UI-pivots-tv-fidelity-fix — push label `+12px` past the
      // segment's right endpoint so the text reads as inline annotation
      // rather than glued to the line. Clip drops the pill when the
      // endpoint is off-screen left OR when the label would render
      // off-screen right.
      const left = endpointLeft + PIVOT_LABEL_GAP_PX
      if (chartWidth > 0 && (endpointLeft < 0 || left > chartWidth)) continue

      out.push({
        presetId: ind.presetId,
        displayKey: dk,
        left,
        top,
        color: meta.color,
        textColor: withAlpha(meta.color || '#9E9E9E', 0.72),
        text: `${dk} (${formatPrice(meta.price, meta.priceScaleMinMove)})`,
        // CE-04g-UI-pivots-rth-and-style-fixes — per-pill fontSize
        // sourced from bucket-level metadata so all 7 levels share the
        // indicator's font_size choice. Falls back to 11 (the default).
        fontSize: Number.isFinite(bucket.fontSize) ? bucket.fontSize : 11,
      })
    }
  }
  return out
}
